/**
 * Hooks TanStack Query — humanizer planner (PR3 — 2026-05-10).
 *
 * Fragmentation des invalidations :
 *   - `pacing` query key invalide UI Diffusion tab (cards plateforme).
 *   - `windows` query key idem.
 *   - `queue.calendar` invalide la vue calendrier QueuePage.
 *   - `queue.forceNowPreview` jamais cachée (snapshot ponctuel).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { humanizerApi } from '@/utils/api/humanizer'
import type {
  DragDropScheduledForRequest,
  PublishingPacingUpdateRequest,
  PublishingWindowsUpdateRequest,
} from '@/utils/api/humanizer'
import type { components } from '@/types/api-generated'

type Platform = components['schemas']['Platform']

interface ApiError {
  detail?: string
}

export const humanizerKeys = {
  all: ['humanizer'] as const,
  pacing: () => [...humanizerKeys.all, 'pacing'] as const,
  windows: (platform?: Platform) => [...humanizerKeys.all, 'windows', platform ?? 'all'] as const,
  calendar: (fromIso: string, toIso: string) => ['queue', 'calendar', fromIso, toIso] as const,
  forceNowPreview: (queueItemId: number) => ['queue', 'force-now-preview', queueItemId] as const,
}

// ── Pacing ──────────────────────────────────────────────────────────────

export function usePublishingPacing() {
  return useQuery({
    queryKey: humanizerKeys.pacing(),
    queryFn: () => humanizerApi.listPacing(),
    staleTime: 60_000,
  })
}

export function useUpsertPublishingPacing() {
  const qc = useQueryClient()
  return useMutation<
    components['schemas']['PublishingPacingResponse'],
    ApiError,
    { platform: Platform; payload: PublishingPacingUpdateRequest }
  >({
    mutationFn: ({ platform, payload }) => humanizerApi.upsertPacing(platform, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: humanizerKeys.pacing() })
    },
  })
}

// ── Windows ─────────────────────────────────────────────────────────────

export function usePublishingWindows(platform?: Platform) {
  return useQuery({
    queryKey: humanizerKeys.windows(platform),
    queryFn: () => humanizerApi.listWindows(platform),
    staleTime: 60_000,
  })
}

export function useUpsertPublishingWindows() {
  const qc = useQueryClient()
  return useMutation<
    components['schemas']['PublishingWindowsListResponse'],
    ApiError,
    PublishingWindowsUpdateRequest
  >({
    mutationFn: (payload) => humanizerApi.upsertWindows(payload),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: humanizerKeys.windows(vars.platform),
      })
      void qc.invalidateQueries({
        queryKey: humanizerKeys.windows(),
      })
    },
  })
}

// ── Queue calendar ──────────────────────────────────────────────────────

export function useQueueCalendar(fromIso: string, toIso: string) {
  return useQuery({
    queryKey: humanizerKeys.calendar(fromIso, toIso),
    queryFn: () => humanizerApi.getCalendar(fromIso, toIso),
    staleTime: 30_000,
  })
}

export function usePatchScheduledFor() {
  const qc = useQueryClient()
  return useMutation<
    components['schemas']['QueueItemResponse'],
    ApiError,
    { queueItemId: number; payload: DragDropScheduledForRequest }
  >({
    mutationFn: ({ queueItemId, payload }) => humanizerApi.patchScheduledFor(queueItemId, payload),
    onSuccess: () => {
      // Invalide tout le calendrier — fenêtres distinctes.
      void qc.invalidateQueries({ queryKey: ['queue', 'calendar'] })
    },
  })
}

export function useForceNowPreview(queueItemId: number | null) {
  return useQuery({
    queryKey:
      queueItemId !== null
        ? humanizerKeys.forceNowPreview(queueItemId)
        : ['queue', 'force-now-preview', 'disabled'],
    queryFn: () => humanizerApi.getForceNowPreview(queueItemId as number),
    enabled: queueItemId !== null && queueItemId > 0,
    staleTime: 0, // toujours fresh — preview est un snapshot ponctuel
  })
}
